<template>
  <div class="rag-monitor-page">
    <HeaderPanel />
    <main class="rag-monitor-main">
      <header class="rag-header">
        <h1 class="page-title">RAG Monitor</h1>
        <p class="page-subtitle">Метрики, логи и сессии RAG-пайплайна</p>
      </header>

      <nav class="rag-nav" aria-label="RAG Monitor sections">
        <div v-if="!isMobile" class="tab-links">
          <router-link
            v-for="tab in tabItems"
            :key="tab.to"
            :to="tab.to"
            class="tab-link"
            :class="{ 'tab-link-active': isTabActive(tab.to) }"
          >
            <i :class="tab.icon" aria-hidden="true" />
            {{ tab.label }}
          </router-link>
        </div>
        <Select
          v-else
          :model-value="currentTabPath"
          :options="tabSelectOptions"
          option-label="label"
          option-value="value"
          class="mobile-tab-select"
          @update:model-value="onMobileTab"
        />
      </nav>

      <div class="rag-content">
        <router-view />
      </div>
    </main>
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import Select from 'primevue/select';
import HeaderPanel from '@/components/HeaderPanel.vue';

const route = useRoute();
const router = useRouter();
const isMobile = ref(false);

const tabItems = [
  { label: 'Dashboard', icon: 'pi pi-chart-line', to: '/rag-monitor/dashboard' },
  { label: 'Execution Logs', icon: 'pi pi-list', to: '/rag-monitor/logs' },
  { label: 'Sessions', icon: 'pi pi-comments', to: '/rag-monitor/sessions' }
];

const tabSelectOptions = tabItems.map((t) => ({ label: t.label, value: t.to }));

const activeTabIndex = computed(() => {
  const path = route.path;
  if (path.includes('/logs')) return 1;
  if (path.includes('/sessions')) return 2;
  return 0;
});

const currentTabPath = computed(() => {
  const idx = activeTabIndex.value;
  return tabItems[idx]?.to || '/rag-monitor/dashboard';
});

function checkMobile() {
  isMobile.value = window.innerWidth < 768;
}

function isTabActive(tabTo) {
  const path = route.path;
  if (tabTo.endsWith('/sessions')) return path.startsWith('/rag-monitor/sessions');
  return path === tabTo || path.startsWith(`${tabTo}/`);
}

function onMobileTab(path) {
  if (path) router.push(path);
}

watch(
  () => route.path,
  (path) => {
    if (path === '/rag-monitor' || path === '/rag-monitor/') {
      router.replace('/rag-monitor/dashboard');
    }
  },
  { immediate: true }
);

onMounted(() => {
  checkMobile();
  window.addEventListener('resize', checkMobile);
});

onUnmounted(() => {
  window.removeEventListener('resize', checkMobile);
});
</script>

<style scoped>
.rag-monitor-page {
  min-height: 100vh;
  background: var(--p-surface-ground);
  color: var(--p-text-color);
}

.rag-monitor-main {
  max-width: 1400px;
  margin: 0 auto;
  padding: 1.25rem 1.25rem 2.5rem;
}

.rag-header {
  margin-bottom: 1.25rem;
}

.page-title {
  margin: 0 0 0.25rem;
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--p-text-color);
  letter-spacing: -0.02em;
}

.page-subtitle {
  margin: 0;
  font-size: 0.9rem;
  color: var(--p-text-muted-color);
}

.rag-nav {
  margin-bottom: 1.5rem;
}

.tab-links {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  padding: 0.25rem;
  border-radius: 10px;
  background: var(--p-content-hover-background);
  border: 1px solid var(--p-content-border-color);
}

.tab-link {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  text-decoration: none;
  color: var(--p-text-muted-color);
  font-size: 0.875rem;
  font-weight: 500;
  border: none;
  background: transparent;
  transition: background 0.15s ease, color 0.15s ease;
}

.tab-link:hover {
  color: var(--p-text-color);
  background: var(--p-content-background);
}

.tab-link-active {
  background: var(--p-content-background);
  color: var(--p-text-color);
  box-shadow: 0 1px 2px color-mix(in srgb, var(--p-text-color) 12%, transparent);
}

.tab-link-active:hover {
  color: var(--p-text-color);
}

.mobile-tab-select {
  width: 100%;
  max-width: 360px;
}

.rag-content {
  min-height: 200px;
}

</style>
